/**
 * Fixture-group membership predicates for vanity assemblies.
 *
 * Every predicate follows the convention used across the codebase:
 * type match is case-insensitive substring over both `obj.type` and
 * `obj.category` keys (via `typeMatches`); the `*NotFaucet` variants
 * exclude faucets that would otherwise match the `sink` pattern.
 *
 * Pure — no side effects.
 */

const { VANITY_DIMENSIONS } = require('./constants');
const { isLowerCabinetLike } = require('../group-customization/anchorTypes');
const { isPointInPolygon } = require('./placementUtils');
const {
  isSink,
  isKitchenSink,
  getSinkType,
  COUNTERTOP_MOUNTED_SINK_TYPES,
} = require('../sink-types');
const { footprintFor } = require('../geometry/objectFootprint');
const { isVanityRunId } = require('../cabinet-runs/runIds');
const { vanityExtent } = require('./vanityExtent');

function typeMatches(obj, pattern) {
  if (!obj) return false;
  const type = (obj.type || '').toLowerCase();
  if (type.includes(pattern)) return true;
  const cat = obj.category;
  if (!cat) return false;
  for (const key of Object.keys(cat)) {
    if (key.toLowerCase().includes(pattern)) return true;
  }
  return false;
}

/**
 * Is this fixture-group id a VANITY assembly's? Both producers mint the
 * `vanity_` prefix — `assembleVanity` (`vanity_<timestamp>_<roomId>`) and
 * the scan path (`vanity_<cabinet.id>`, bathroom-post-processing Step 1b) —
 * and a standalone sink's group is `standalone_<sinkId>`. Prefix tests on
 * group ids are the established convention here (the bay-claims heal keys
 * on `kitchenIsland_` the same way).
 *
 * Consumer that motivated it: the cabinet-run membership rule's
 * wall-reference fallback (`runCabinetryMembership`) — a vanity cabinet is a
 * `lower-cabinet` with `parentWallIds`, so a run drawn on the same bathroom
 * wall swept the unrelated vanity into the run's appearance scope.
 */
function isVanityAssemblyGroupId(fixtureGroupId) {
  return (
    typeof fixtureGroupId === 'string' &&
    (fixtureGroupId.startsWith('vanity_') ||
      fixtureGroupId.startsWith('standalone_'))
  );
}

function inGroup(obj, fixtureGroupId) {
  return obj?.fixtureGroupId === fixtureGroupId;
}

function getFixtureGroupMembers(objects, fixtureGroupId) {
  return objects.filter((o) => inGroup(o, fixtureGroupId));
}

/**
 * Is this object a BASIN — never the cabinet one stands in.
 *
 * `typeMatches` is a substring test over `type` AND every `category` key, and
 * a cabinet-run sink bay carries `category.sinkBase` (`applyBayType`), so
 * `'sink'` matched the base CABINET and the group's "sinks" included a
 * 30"-wide box. Everything downstream then measured the installation from the
 * cabinet's centreline instead of the bowl's: `emitVanityLinks` paired a
 * faucet to it, and a mirror plan centred on it. Run bays did not exist when
 * this predicate was written, which is why the substring was harmless then —
 * the trap is recorded in `kitchen-assembly/fixtureGroup.js`'s
 * `isSinkFaucetLike`.
 *
 * A basin is not a cabinet body, so excluding one is a statement about the
 * vocabulary rather than a special case: no scan- or chat-built vanity object
 * is both, which is why this changes nothing outside a run group.
 *
 * The component swap (`swap_fixture`) carried its own copies of the bare
 * substring test and so missed this fix: a sink swap rewrote a run's 60"
 * sink base into a third vessel sink (2026-10-29). Every "which of these are
 * the sinks" question about a vanity group asks this predicate.
 */
function isGroupBasin(obj) {
  return (
    typeMatches(obj, 'sink') &&
    !typeMatches(obj, 'faucet') &&
    !isLowerCabinetLike(obj)
  );
}

/** The basins in a group — see `isGroupBasin`. */
function getFixtureGroupSinks(objects, fixtureGroupId) {
  return objects.filter((o) => inGroup(o, fixtureGroupId) && isGroupBasin(o));
}

function getFixtureGroupFaucets(objects, fixtureGroupId) {
  return objects.filter(
    (o) => inGroup(o, fixtureGroupId) && typeMatches(o, 'faucet')
  );
}

function getFixtureGroupMirrors(objects, fixtureGroupId) {
  return objects.filter(
    (o) => inGroup(o, fixtureGroupId) && typeMatches(o, 'mirror')
  );
}

function getFixtureGroupCeilingLights(objects, fixtureGroupId) {
  return objects.filter(
    (o) => inGroup(o, fixtureGroupId) && typeMatches(o, 'ceiling-light')
  );
}

function getFixtureGroupOverheadLights(objects, fixtureGroupId) {
  return objects.filter(
    (o) => inGroup(o, fixtureGroupId) && typeMatches(o, 'overhead')
  );
}

/**
 * True for any vanity-assembly light fixture — sconce, overhead bar,
 * vanity-light bar, lightbar, ceiling-light, or pendant. Matches the
 * full set that `swap_fixture(componentType='light')` and the design
 * center's "Vanity Light" tile route through. Single source of truth
 * so add / swap / remove paths can't drift on the type list.
 */
function isAssemblyLight(obj) {
  return (
    typeMatches(obj, 'wall-light') ||
    typeMatches(obj, 'sconce') ||
    typeMatches(obj, 'vanity-light') ||
    typeMatches(obj, 'lightbar') ||
    typeMatches(obj, 'overhead') ||
    typeMatches(obj, 'ceiling-light')
  );
}

function getFixtureGroupLights(objects, fixtureGroupId) {
  return objects.filter(
    (o) => inGroup(o, fixtureGroupId) && isAssemblyLight(o)
  );
}

/**
 * Expansion margin for the "is this light over the vanity?" check.
 * 4" along the wall and 4" into the room covers typical pendant /
 * ceiling-light placement slop without sweeping in genuinely
 * ambient lights placed by `add_lighting`.
 */
const ORPHAN_LIGHT_FOOTPRINT_MARGIN_M = 0.1016;

/**
 * Ceiling-lights with no `fixtureGroupId` that sit over the vanity's
 * footprint — the orphan case where a pendant (or ambient ceiling-light
 * converted to a pendant via the model picker) is logically the
 * vanity's light but never got tagged. `add_vanity_light` and the
 * swap path use this to tear them down alongside the assembly-tagged
 * lights so the user doesn't end up with both an orphan pendant and
 * the freshly-placed sconces / bar.
 *
 * Pure — returns the matching objects.
 */
function findOrphanVanityCeilingLights(objects, vanityCabinet) {
  if (!vanityCabinet?.center || !vanityCabinet?.dimensions) return [];
  const corners = footprintFor(
    vanityCabinet.center,
    (vanityCabinet.dimensions.x || 0) + 2 * ORPHAN_LIGHT_FOOTPRINT_MARGIN_M,
    (vanityCabinet.dimensions.z || 0) + 2 * ORPHAN_LIGHT_FOOTPRINT_MARGIN_M,
    vanityCabinet.rotation?.y || 0
  );
  return (objects || []).filter((o) => {
    if (!o || o.fixtureGroupId) return false;
    if ((o.type || '').toLowerCase() !== 'ceiling-light') return false;
    if (!o.center) return false;
    return isPointInPolygon({ x: o.center.x, z: o.center.z }, corners);
  });
}

/**
 * Overhead-wall-mounted lights: catalog `overhead-wall-light` and
 * `vanity-light` bars both sit above the mirror with the same Y rule
 * (`computeOverheadLightCenterY`). Everything else is treated as a
 * sconce by default.
 */
function isOverheadLight(obj) {
  return typeMatches(obj, 'overhead');
}

/**
 * Classify a catalog model into one of {sconce, pendant, bar, ceiling}.
 * Prefers explicit `lightStyle`, then catalog `type`, then tags. Shared
 * by `addVanityLight` (pre-build dispatch) and `swapComponent` (mirror
 * gate on the swap path) so the two entry points can't drift on which
 * styles need a mirror.
 */
function classifyLightStyle(model) {
  if (!model) return 'ceiling';
  if (model.lightStyle) return model.lightStyle;
  const t = model.type || '';
  if (t === 'wall-light') return 'sconce';
  if (t === 'overhead-wall-light' || t === 'vanity-light') return 'bar';
  if (t === 'ceiling-light') {
    const name = (model.name || '').toLowerCase();
    const tags = (model.tags || []).map((s) => String(s).toLowerCase());
    if (name.includes('pendant') || tags.includes('pendant')) return 'pendant';
    return 'ceiling';
  }
  return 'ceiling';
}

/**
 * True for the light styles that anchor to a vanity mirror (sconce
 * flanks the mirror; bar sits above it). Pendant + ceiling hang from
 * the ceiling and don't need a mirror.
 */
function lightStyleRequiresMirror(style) {
  return style === 'sconce' || style === 'bar';
}

/**
 * Wall-mounted sconce beside the mirror. Catches `sconce` and generic
 * `wall-light` types that aren't the overhead variants.
 */
function isSconce(obj) {
  if (isOverheadLight(obj)) return false;
  return typeMatches(obj, 'sconce') || typeMatches(obj, 'wall-light');
}

/**
 * Parents chain for a vanity mirror: prefer the faucet the mirror
 * sits above so the full chain reads cabinet → countertop → sink →
 * faucet → mirror → light. Fall back to the cabinet so there's
 * always a reachable root.
 */
function pickMirrorParents(faucet, cabinet) {
  if (faucet) return [{ id: faucet.id, type: 'bathroom-sink-faucet' }];
  if (cabinet) return [{ id: cabinet.id, type: 'lower-cabinet' }];
  return [];
}

/**
 * Parents chain for a vanity light (sconce, overhead, or light bar):
 * prefer the nearest mirror; fall back to the cabinet.
 */
function pickLightParents(mirror, cabinet) {
  if (mirror) return [{ id: mirror.id, type: 'mirror' }];
  if (cabinet) return [{ id: cabinet.id, type: 'lower-cabinet' }];
  return [];
}

/**
 * Canonical classifier for a vanity's lower cabinet within a fixture
 * group. Matches `optimize_vanity`'s existing categorization.
 */
/** The compact one-piece unit assembled in place of a too-small vanity. */
function isWallHungSink(obj) {
  return obj?.type === 'sink' && obj.sinkType === 'wall-mount';
}

/**
 * A sink that stands on its own — pedestal or wall-hung — rather than
 * sitting in a countertop. THE predicate for "this sink IS the vanity
 * body": it has no cabinet under it, its top is the rim rather than a
 * counter, and it is a catalog product that must not be stretched.
 *
 * Derived from `COUNTERTOP_MOUNTED_SINK_TYPES` rather than listing the
 * standalone mounts, so a new mount type added to the canonical contract
 * in `shared-lib/sink-types` is classified correctly here by default.
 * Kitchen sinks are excluded outright — they are always in a counter, and
 * their `subtype` vocabulary is a different namespace.
 */
function isStandaloneSink(obj) {
  if (!isSink(obj) || isKitchenSink(obj)) return false;
  const mount = getSinkType(obj);
  if (!mount) return false;
  return !COUNTERTOP_MOUNTED_SINK_TYPES.includes(mount);
}

function isLowerCabinet(obj) {
  // Delegates to the shared classifier so the writer (this module) and the
  // anchor picker can never disagree again (round-2 audit M1).
  return isLowerCabinetLike(obj);
}

/**
 * The vanity's body — the object accessories (mirror, lights) hang over
 * and resize/links anchor to. A lower cabinet when the group has one;
 * otherwise the standalone sink (pedestal / wall-hung), which is its own
 * body and has no cabinet under it.
 *
 * The STORED fact comes first: whoever builds a group stamps the body its
 * links are written against `groupAnchor` (group-customization's
 * `pickAnchor` reads the same stamp). A double vanity built in a cabinet line
 * can hold two sink bases, and the stamp, not array order, says which.
 */
function getVanityCabinet(fixtureGroupObjects) {
  return (
    fixtureGroupObjects.find((o) => o?.groupAnchor && isLowerCabinet(o)) ||
    fixtureGroupObjects.find(isLowerCabinet) ||
    fixtureGroupObjects.find(isStandaloneSink)
  );
}

/**
 * Every vanity in a room, as `{ fixtureGroupId, cabinet }` — what the Design
 * Center and a design package style (all of them, the user 2026-10-30; the
 * package's price already counted every one).
 *
 * `getVanityCabinet` answers for ONE group; handed a whole room it returns
 * the room's first lower cabinet. On a vanity rebuilt as a cabinet run that
 * is a drawer base in a group of its own (`kitchenCabinet_…`), while the
 * mirrors and lights hang in the vanity's group beside the sink base — so a
 * vanity light was refused "Requires a mirror" under the very mirrors it
 * should have hung over (2026-10-29). A vanity is where its basin is: one per
 * group holding a bathroom basin. A room whose vanity has lost its basin
 * still has that vanity — the first body, as before.
 */
function findRoomVanities(roomObjects = []) {
  const groupIds = [
    ...new Set(
      roomObjects
        .filter((o) => isGroupBasin(o) && !isKitchenSink(o))
        .map((o) => o.fixtureGroupId)
        .filter(Boolean)
    ),
  ];
  const vanities = groupIds
    .map((fixtureGroupId) => ({
      fixtureGroupId,
      cabinet: getVanityCabinet(
        getFixtureGroupMembers(roomObjects, fixtureGroupId)
      ),
    }))
    .filter((v) => v.cabinet);
  if (vanities.length) return vanities;
  const cabinet = getVanityCabinet(roomObjects);
  return cabinet ? [{ fixtureGroupId: cabinet.fixtureGroupId, cabinet }] : [];
}

/** The room's first vanity body (`findRoomVanities`). */
function findRoomVanityCabinet(roomObjects = []) {
  return findRoomVanities(roomObjects)[0]?.cabinet;
}

/**
 * The objects of a vanity group's cabinet RUN that sit outside the group.
 *
 * A vanity built in a cabinet line is a run (`vanityToRun`): its drawer
 * stacks and fillers take groups of their own and its countertop is the
 * run's — the run's id and no `fixtureGroupId` — so they are reached through
 * the run, as `removeAssembly` reaches them to take them with the vanity.
 */
function vanityRunMembers(roomObjects, groupObjects) {
  const runIds = new Set(
    groupObjects.map((o) => o?.runId).filter((id) => isVanityRunId(id))
  );
  if (!runIds.size) return [];
  const inGroup = new Set(groupObjects);
  return roomObjects.filter((o) => runIds.has(o?.runId) && !inGroup.has(o));
}

/**
 * Everything that goes when `member`'s fixture goes: its fixture group,
 * widened through a VANITY run (`vanityToRun`) to the whole vanity, as
 * `{ objects, runIds }`.
 *
 * A vanity built as a cabinet run is several groups: the sink bases, basins,
 * faucets, mirrors and lights share the vanity's group, each drawer base and
 * filler has a group of its own, and the countertop is the run's (its id, no
 * group). So from a drawer base the group alone is one box, and from a basin
 * it is the vanity without its drawers — removing either left the rest
 * standing (2026-11-03). Groups and vanity runs are followed until nothing
 * new joins, so any member gives the same answer. A kitchen run is never
 * followed: a kitchen cabinet's assembly is its own group.
 *
 * Every object returned is one of `roomObjects`; `runIds` are the vanity runs
 * the assembly holds, for a caller that drops the run with its cabinetry.
 */
function assemblyOf(roomObjects = [], member) {
  if (!member) return { objects: [], runIds: [] };
  const groups = new Set();
  const runs = new Set();
  const join = (o) => {
    if (o.fixtureGroupId) groups.add(o.fixtureGroupId);
    if (isVanityRunId(o.runId)) runs.add(o.runId);
  };
  join(member);
  if (!groups.size && !runs.size) {
    const self = roomObjects.filter(
      (o) => o === member || (member.id && o?.id === member.id)
    );
    return { objects: self, runIds: [] };
  }
  let objects = [];
  let count = -1;
  while (objects.length !== count) {
    count = objects.length;
    objects = roomObjects.filter(
      (o) => o && (groups.has(o.fixtureGroupId) || runs.has(o.runId))
    );
    objects.forEach(join);
  }
  return { objects, runIds: [...runs] };
}

/**
 * The countertop a vanity group's basins stand in: the group's own, or — on
 * a vanity rebuilt as a cabinet run — the run's. Missing the run's, the
 * swaps fell back to the cabinet: a vessel seated 1.5" into the slab, a
 * wall-mount faucet at the cabinet's mid-height (2026-10-29).
 */
function findVanityCountertop(roomObjects, groupObjects) {
  return (
    getVanityCountertop(groupObjects) ||
    getVanityCountertop(vanityRunMembers(roomObjects, groupObjects))
  );
}

/**
 * Canonical classifier for a vanity countertop within a fixture group.
 */
function getVanityCountertop(fixtureGroupObjects) {
  return fixtureGroupObjects.find(
    (obj) =>
      obj.type === 'countertop' ||
      obj.type === 'vanity-countertop' ||
      obj.category?.countertop
  );
}

/**
 * Top-of-countertop Y for mirror / wall-space math. Prefers the real
 * countertop geometry; falls back to cabinet top + the canonical
 * countertop thickness (which is how `assembleVanity` places the
 * countertop at creation time).
 */
function computeCountertopTopY(countertop, cabinet) {
  const thickness = VANITY_DIMENSIONS.countertop.thickness;
  if (countertop) {
    return countertop.center.y + (countertop.dimensions?.y || thickness) / 2;
  }
  if (cabinet) {
    const top = cabinet.center.y + (cabinet.dimensions?.y || 0.8) / 2;
    // A standalone sink's top IS the rim; a cabinet carries a counter on it.
    return isStandaloneSink(cabinet) ? top : top + thickness;
  }
  return null;
}

/**
 * The per-vanity context every accessory flow starts from — THE reader of
 * what a vanity is made of, for both shapes a vanity has: one group holding
 * cabinet, counter and fixtures (`build_vanity`), and a cabinet run whose
 * boxes and counter sit partly outside the group. One shot so
 * `optimize_vanity`, the swaps, the add and fit tools and
 * `selectMirrorAndLighting` can't drift on classifier choice. Any field can
 * be null if unresolvable; callers decide how to handle missing pieces.
 *
 *   cabinet     the body (`getVanityCabinet`)
 *   countertop  the group's or the run's (`findVanityCountertop`)
 *   sinks       the basins (`isGroupBasin` — never a run's sink base)
 *   runMembers  the run's objects outside the group
 *   extent      `{ center, width }` of all the vanity's lower boxes along its
 *               wall (`vanityExtent`) — measure mirrors and lights from this,
 *               not from the body, which on a run is one box of several
 */
function deriveVanityCoreContext(roomObjects, roomWalls, fixtureGroupId) {
  const objects = roomObjects || [];
  const group = getFixtureGroupMembers(objects, fixtureGroupId);
  const cabinet = getVanityCabinet(group);
  const parentWallId = cabinet?.parentWallIds?.[0];
  const parentWall = parentWallId
    ? (roomWalls || []).find((w) => w.id === parentWallId) || null
    : null;
  const runMembers = vanityRunMembers(objects, group);
  const countertop =
    getVanityCountertop(group) || getVanityCountertop(runMembers);
  const sinks = group.filter(isGroupBasin);
  const countertopTopY = computeCountertopTopY(countertop, cabinet);
  // Tall units stand beside a vanity, not under its counter or mirror.
  const boxes = [...group, ...runMembers].filter(
    (o) => isLowerCabinet(o) && !o.category?.tall
  );
  const extent = cabinet ? vanityExtent(cabinet, boxes) : null;
  return {
    group,
    cabinet,
    parentWall,
    countertop,
    sinks,
    countertopTopY,
    runMembers,
    extent,
  };
}

module.exports = {
  typeMatches,
  isVanityAssemblyGroupId,
  getFixtureGroupMembers,
  isGroupBasin,
  getFixtureGroupSinks,
  getFixtureGroupFaucets,
  getFixtureGroupMirrors,
  getFixtureGroupCeilingLights,
  getFixtureGroupOverheadLights,
  getFixtureGroupLights,
  findOrphanVanityCeilingLights,
  isAssemblyLight,
  getVanityCabinet,
  findRoomVanities,
  findRoomVanityCabinet,
  vanityRunMembers,
  assemblyOf,
  findVanityCountertop,
  isWallHungSink,
  isStandaloneSink,
  getVanityCountertop,
  isOverheadLight,
  isSconce,
  classifyLightStyle,
  lightStyleRequiresMirror,
  pickMirrorParents,
  pickLightParents,
  computeCountertopTopY,
  deriveVanityCoreContext,
};
